import React, { useEffect, useState, memo } from 'react';
import { Trophy, Zap, Clock, Gift, Star, X, Heart, Repeat, ArrowRight, CheckCircle2 } from 'lucide-react';
// Game components
const CandyCrusher = ({
  onComplete
}: {
  onComplete: (score: number) => void;
}) => {
  const [grid, setGrid] = useState<string[][]>([]);
  const [score, setScore] = useState(0);
  const [selectedCell, setSelectedCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const symbols = ['🍒', '🍓', '🍇', '🍑', '🍎', '🍌', '💋', '❤️'];
  // Initialize grid
  useEffect(() => {
    const newGrid = Array(8).fill(0).map(() => Array(8).fill(0).map(() => symbols[Math.floor(Math.random() * symbols.length)]));
    setGrid(newGrid);
  }, []);
  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0) {
      onComplete(score);
      return;
    }
    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, score, onComplete]);
  // Check for matches after each move
  useEffect(() => {
    const checkMatches = () => {
      let matchFound = false;
      let newGrid = [...grid];
      let points = 0;
      // Check rows
      for (let row = 0; row < 8; row++) {
        for (let col = 0; col < 6; col++) {
          if (grid[row][col] && grid[row][col] === grid[row][col + 1] && grid[row][col] === grid[row][col + 2]) {
            matchFound = true;
            points += 10;
            newGrid[row][col] = '';
            newGrid[row][col + 1] = '';
            newGrid[row][col + 2] = '';
          }
        }
      }
      // Check columns
      for (let col = 0; col < 8; col++) {
        for (let row = 0; row < 6; row++) {
          if (grid[row][col] && grid[row][col] === grid[row + 1][col] && grid[row][col] === grid[row + 2][col]) {
            matchFound = true;
            points += 10;
            newGrid[row][col] = '';
            newGrid[row + 1][col] = '';
            newGrid[row + 2][col] = '';
          }
        }
      }
      if (matchFound) {
        // Fill in empty spaces
        for (let col = 0; col < 8; col++) {
          let emptySpaces = 0;
          for (let row = 7; row >= 0; row--) {
            if (newGrid[row][col] === '') {
              emptySpaces++;
            } else if (emptySpaces > 0) {
              newGrid[row + emptySpaces][col] = newGrid[row][col];
              newGrid[row][col] = '';
            }
          }
          // Fill top with new symbols
          for (let row = 0; row < 8; row++) {
            if (newGrid[row][col] === '') {
              newGrid[row][col] = symbols[Math.floor(Math.random() * symbols.length)];
            }
          }
        }
        setGrid(newGrid);
        setScore(score + points);
      }
    };
    if (grid.length > 0) {
      const timeout = setTimeout(checkMatches, 300);
      return () => clearTimeout(timeout);
    }
  }, [grid, score]);
  const handleCellClick = (row: number, col: number) => {
    if (selectedCell === null) {
      setSelectedCell({
        row,
        col
      });
    } else {
      // Check if cells are adjacent
      const isAdjacent = Math.abs(selectedCell.row - row) === 1 && selectedCell.col === col || Math.abs(selectedCell.col - col) === 1 && selectedCell.row === row;
      if (isAdjacent) {
        // Swap cells
        const newGrid = [...grid];
        const temp = newGrid[selectedCell.row][selectedCell.col];
        newGrid[selectedCell.row][selectedCell.col] = newGrid[row][col];
        newGrid[row][col] = temp;
        setGrid(newGrid);
      }
      setSelectedCell(null);
    }
  };
  return <div className="p-4 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl" data-id="element-7708">
      <div className="flex justify-between mb-4" data-id="element-7709">
        <div className="flex items-center gap-2" data-id="element-7710">
          <Star size={16} className="text-yellow-400" data-id="element-7711" />
          <span className="font-bold" data-id="element-7712">Score: {score}</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-7713">
          <Clock size={16} className="text-red-400" data-id="element-7714" />
          <span className="font-bold" data-id="element-7715">{timeLeft}s</span>
        </div>
      </div>
      <div className="grid grid-cols-8 gap-1 mb-4 bg-black/20 p-2 rounded-lg" data-id="element-7716">
        {grid.map((row, rowIndex) => row.map((cell, colIndex) => <button key={`${rowIndex}-${colIndex}`} className={`w-9 h-9 flex items-center justify-center rounded-md text-xl transition-all ${selectedCell?.row === rowIndex && selectedCell?.col === colIndex ? 'bg-pink-500/50 scale-110' : 'bg-white/10 hover:bg-white/20'}`} onClick={() => handleCellClick(rowIndex, colIndex)} data-id="element-7717">
              {cell}
            </button>))}
      </div>
      <p className="text-sm text-center text-gray-300" data-id="element-7718">
        Swap adjacent candies to make matches of 3 or more
      </p>
    </div>;
};
const PleasurePath = ({
  onComplete
}: {
  onComplete: (score: number) => void;
}) => {
  const [grid, setGrid] = useState<number[][]>([]);
  const [path, setPath] = useState<{
    row: number;
    col: number;
  }[]>([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameOver, setGameOver] = useState(false);
  // Initialize grid
  useEffect(() => {
    const newGrid = Array(6).fill(0).map(() => Array(6).fill(0).map(() => Math.floor(Math.random() * 3)));
    setGrid(newGrid);
    // Start with top-left
    setPath([{
      row: 0,
      col: 0
    }]);
  }, []);
  // Timer countdown
  useEffect(() => {
    if (timeLeft <= 0 || gameOver) {
      onComplete(score);
      return;
    }
    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, score, gameOver, onComplete]);
  const handleCellClick = (row: number, col: number) => {
    if (gameOver) return;
    const lastCell = path[path.length - 1];
    // Check if cell is adjacent to last cell in path
    const isAdjacent = Math.abs(lastCell.row - row) === 1 && lastCell.col === col || Math.abs(lastCell.col - col) === 1 && lastCell.row === row;
    if (isAdjacent) {
      // Add to path
      const newPath = [...path, {
        row,
        col
      }];
      setPath(newPath);
      setScore(score + 5 + grid[row][col] * 5);
      // Check if reached bottom-right
      if (row === 5 && col === 5) {
        setScore(score + 50);
        setGameOver(true);
      }
    }
  };
  const getColor = (value: number) => {
    switch (value) {
      case 0:
        return 'bg-blue-500/30';
      case 1:
        return 'bg-purple-500/30';
      case 2:
        return 'bg-pink-500/30';
      default:
        return 'bg-blue-500/30';
    }
  };
  const isInPath = (row: number, col: number) => {
    return path.some(cell => cell.row === row && cell.col === col);
  };
  return <div className="p-4 bg-gradient-to-br from-blue-900/20 to-cyan-900/20 rounded-xl" data-id="element-7719">
      <div className="flex justify-between mb-4" data-id="element-7720">
        <div className="flex items-center gap-2" data-id="element-7721">
          <Star size={16} className="text-yellow-400" data-id="element-7722" />
          <span className="font-bold" data-id="element-7723">Score: {score}</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-7724">
          <Clock size={16} className="text-red-400" data-id="element-7725" />
          <span className="font-bold" data-id="element-7726">{timeLeft}s</span>
        </div>
      </div>
      <div className="grid grid-cols-6 gap-1 mb-4 bg-black/20 p-2 rounded-lg" data-id="element-7727">
        {grid.map((row, rowIndex) => row.map((cell, colIndex) => <button key={`${rowIndex}-${colIndex}`} className={`w-12 h-12 flex items-center justify-center rounded-md text-xl transition-all ${isInPath(rowIndex, colIndex) ? 'bg-cyan-500/70 scale-105' : `${getColor(cell)} hover:bg-white/20`}`} onClick={() => handleCellClick(rowIndex, colIndex)} data-id="element-7728">
              {isInPath(rowIndex, colIndex) && <div className="w-3 h-3 bg-white rounded-full" data-id="element-7729"></div>}
              {rowIndex === 5 && colIndex === 5 && '🎯'}
            </button>))}
      </div>
      <p className="text-sm text-center text-gray-300" data-id="element-7730">
        Create a path from top-left to bottom-right
      </p>
    </div>;
};
const StripDice = ({
  onComplete
}: {
  onComplete: (score: number) => void;
}) => {
  const [dice1, setDice1] = useState(1);
  const [dice2, setDice2] = useState(1);
  const [rolling, setRolling] = useState(false);
  const [score, setScore] = useState(0);
  const [rollsLeft, setRollsLeft] = useState(5);
  const rollDice = () => {
    if (rolling || rollsLeft <= 0) return;
    setRolling(true);
    // Animate dice roll
    const rolls = 10;
    let currentRoll = 0;
    const rollInterval = setInterval(() => {
      setDice1(Math.floor(Math.random() * 6) + 1);
      setDice2(Math.floor(Math.random() * 6) + 1);
      currentRoll++;
      if (currentRoll >= rolls) {
        clearInterval(rollInterval);
        setRolling(false);
        // Calculate score based on final dice values
        const newDice1 = Math.floor(Math.random() * 6) + 1;
        const newDice2 = Math.floor(Math.random() * 6) + 1;
        setDice1(newDice1);
        setDice2(newDice2);
        const rollScore = calculateScore(newDice1, newDice2);
        setScore(score + rollScore);
        setRollsLeft(rollsLeft - 1);
        if (rollsLeft === 1) {
          setTimeout(() => {
            onComplete(score + rollScore);
          }, 1500);
        }
      }
    }, 100);
  };
  const calculateScore = (d1: number, d2: number) => {
    const total = d1 + d2;
    // Doubles get bonus points
    if (d1 === d2) {
      return total * 3;
    }
    // 7 is lucky
    if (total === 7) {
      return 20;
    }
    // 11 is also lucky
    if (total === 11) {
      return 25;
    }
    return total;
  };
  const getDiceFace = (value: number) => {
    switch (value) {
      case 1:
        return '⚀';
      case 2:
        return '⚁';
      case 3:
        return '⚂';
      case 4:
        return '⚃';
      case 5:
        return '⚄';
      case 6:
        return '⚅';
      default:
        return '⚀';
    }
  };
  return <div className="p-4 bg-gradient-to-br from-amber-900/20 to-red-900/20 rounded-xl" data-id="element-7731">
      <div className="flex justify-between mb-4" data-id="element-7732">
        <div className="flex items-center gap-2" data-id="element-7733">
          <Star size={16} className="text-yellow-400" data-id="element-7734" />
          <span className="font-bold" data-id="element-7735">Score: {score}</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-7736">
          <Repeat size={16} className="text-amber-400" data-id="element-7737" />
          <span className="font-bold" data-id="element-7738">Rolls: {rollsLeft}</span>
        </div>
      </div>
      <div className="flex justify-center gap-6 mb-6" data-id="element-7739">
        <div className={`w-20 h-20 bg-white rounded-xl flex items-center justify-center text-4xl ${rolling ? 'animate-bounce' : ''}`} data-id="element-7740">
          {getDiceFace(dice1)}
        </div>
        <div className={`w-20 h-20 bg-white rounded-xl flex items-center justify-center text-4xl ${rolling ? 'animate-bounce' : ''}`} data-id="element-7741">
          {getDiceFace(dice2)}
        </div>
      </div>
      <button className={`w-full py-3 rounded-lg text-white font-medium ${rolling || rollsLeft <= 0 ? 'bg-gray-500' : 'bg-gradient-to-r from-amber-600 to-red-600 hover:from-amber-500 hover:to-red-500'}`} onClick={rollDice} disabled={rolling || rollsLeft <= 0} data-id="element-7742">
        {rolling ? 'Rolling...' : rollsLeft > 0 ? 'Roll Dice' : 'Game Over'}
      </button>
      {rollsLeft > 0 && <p className="text-sm text-center text-gray-300 mt-3" data-id="element-7743">
          Roll doubles for 3x points! Lucky 7 and 11 for bonus points.
        </p>}
    </div>;
};
const ToySyncRhythm = ({
  onComplete
}: {
  onComplete: (score: number) => void;
}) => {
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [notes, setNotes] = useState<{
    lane: number;
    position: number;
    hit: boolean;
  }[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  // Generate notes
  useEffect(() => {
    if (!isPremium) return;
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        const lane = Math.floor(Math.random() * 4);
        setNotes(prev => [...prev, {
          lane,
          position: 0,
          hit: false
        }]);
      }
      // Move notes down
      setNotes(prev => prev.map(note => ({
        ...note,
        position: note.position + 5
      })).filter(note => note.position < 100));
    }, 100);
    return () => clearInterval(interval);
  }, [isPremium]);
  // Timer countdown
  useEffect(() => {
    if (!isPremium) return;
    if (timeLeft <= 0) {
      onComplete(score);
      return;
    }
    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, score, isPremium, onComplete]);
  const handleLaneClick = (lane: number) => {
    if (!isPremium) return;
    // Check if there's a note in the hit zone
    const hitZone = notes.findIndex(note => note.lane === lane && note.position >= 70 && note.position <= 90 && !note.hit);
    if (hitZone !== -1) {
      // Hit!
      const newNotes = [...notes];
      newNotes[hitZone].hit = true;
      setNotes(newNotes);
      // Calculate score based on accuracy
      const accuracy = 100 - Math.abs(80 - notes[hitZone].position);
      const points = Math.floor(accuracy / 10) * 5;
      // Increase combo
      const newCombo = combo + 1;
      setCombo(newCombo);
      // Add combo bonus
      const comboBonus = Math.floor(newCombo / 5) * 10;
      setScore(score + points + comboBonus);
    } else {
      // Miss - reset combo
      setCombo(0);
    }
  };
  return <div className="p-4 bg-gradient-to-br from-green-900/20 to-emerald-900/20 rounded-xl" data-id="element-7744">
      {!isPremium ? <div className="flex flex-col items-center justify-center py-6" data-id="element-7745">
          <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center mb-4" data-id="element-7746">
            <Star size={24} className="text-purple-300" data-id="element-7747" />
          </div>
          <h3 className="text-lg font-bold mb-2" data-id="element-7748">Premium Game</h3>
          <p className="text-sm text-gray-300 text-center mb-4" data-id="element-7749">
            Upgrade to VIP to access this exclusive rhythm game
          </p>
          <button className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white font-medium" onClick={() => setIsPremium(true)} // For demo purposes
      data-id="element-7750">
            Unlock Now
          </button>
        </div> : <>
          <div className="flex justify-between mb-4" data-id="element-7751">
            <div className="flex items-center gap-2" data-id="element-7752">
              <Star size={16} className="text-yellow-400" data-id="element-7753" />
              <span className="font-bold" data-id="element-7754">Score: {score}</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-7755">
              <Zap size={16} className="text-yellow-400" data-id="element-7756" />
              <span className="font-bold" data-id="element-7757">Combo: {combo}x</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-7758">
              <Clock size={16} className="text-red-400" data-id="element-7759" />
              <span className="font-bold" data-id="element-7760">{timeLeft}s</span>
            </div>
          </div>
          <div className="relative h-64 bg-black/30 rounded-lg overflow-hidden mb-4" data-id="element-7761">
            {/* Note lanes */}
            <div className="grid grid-cols-4 h-full" data-id="element-7762">
              {[0, 1, 2, 3].map(lane => <div key={lane} className="border-r border-white/10 relative" onClick={() => handleLaneClick(lane)} data-id="element-7763">
                  {/* Hit zone */}
                  <div className="absolute bottom-5 left-0 right-0 h-5 bg-white/10" data-id="element-7764"></div>
                </div>)}
            </div>
            {/* Notes */}
            {notes.map((note, index) => <div key={index} className={`absolute w-full h-5 ${note.hit ? 'bg-green-500/50' : 'bg-pink-500/50'} rounded-md`} style={{
          left: `${note.lane * 25}%`,
          width: '25%',
          top: `${note.position}%`,
          display: note.hit ? 'none' : 'block'
        }} data-id="element-7765"></div>)}
          </div>
          <div className="grid grid-cols-4 gap-2" data-id="element-7766">
            {[0, 1, 2, 3].map(lane => <button key={lane} className="py-3 bg-emerald-500/30 hover:bg-emerald-500/50 rounded-lg" onClick={() => handleLaneClick(lane)} data-id="element-7767">
                {['A', 'S', 'K', 'L'][lane]}
              </button>)}
          </div>
        </>}
    </div>;
};
export const GamesSection = () => {
  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [showCongrats, setShowCongrats] = useState(false);
  const [gameScore, setGameScore] = useState(0);
  const [earnedTokens, setEarnedTokens] = useState(0);
  const handleGameComplete = (score: number) => {
    // Calculate tokens based on score
    const tokens = Math.floor(score / 10);
    setGameScore(score);
    setEarnedTokens(tokens);
    setShowCongrats(true);
  };
  const handlePlayAgain = () => {
    setShowCongrats(false);
    // Keep the same game active
  };
  const handleClose = () => {
    setShowCongrats(false);
    setActiveGame(null);
  };
  const navigateToRewards = () => {
    setShowCongrats(false);
    setActiveGame(null);
    // Dispatch a navigate event to go to rewards section
    const navigateEvent = new CustomEvent('navigate', {
      detail: {
        section: 'rewards'
      }
    });
    document.dispatchEvent(navigateEvent);
  };
  const navigateToAngelMatch = () => {
    // Navigate to the dedicated Angel Match game page
    const navigateEvent = new CustomEvent('navigate', {
      detail: {
        section: 'angel_match'
      }
    });
    document.dispatchEvent(navigateEvent);
  };
  return <section className="space-y-6" data-id="element-7768">
      <div className="flex items-center justify-between" data-id="element-7769">
        <h2 className="text-lg font-bold" data-id="element-7770">Games</h2>
        <div className="flex items-center gap-2" data-id="element-7771">
          <div className="flex items-center gap-1" data-id="element-7772">
            <Trophy size={16} className="text-yellow-400" data-id="element-7773" />
            <span className="text-sm" data-id="element-7774">1,240 pts</span>
          </div>
        </div>
      </div>
      {activeGame === null ? <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7775">
            {/* Angel Match Quest - New Featured Game */}
            <div className="bg-gradient-to-br from-pink-900/40 to-purple-900/40 rounded-xl border border-pink-800/50 overflow-hidden col-span-1 sm:col-span-2 mb-2" data-id="element-7776">
              <div className="aspect-video relative" data-id="element-7777">
                <div className="relative rounded-lg overflow-hidden" data-id="element-7778">
                  <img src="https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?q=80&w=1287&auto=format&fit=crop" alt="Angel Match Quest" className="w-full h-full object-cover aspect-[16/9]" data-id="element-7779" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-3" data-id="element-7780">
                    <div className="flex items-center gap-2 mb-2" data-id="element-7781">
                      <span className="px-2 py-0.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold rounded-full" data-id="element-7782">
                        NEW
                      </span>
                      <span className="px-2 py-0.5 bg-yellow-500/80 text-white text-xs font-bold rounded-full" data-id="element-7783">
                        FEATURED
                      </span>
                    </div>
                    <h3 className="font-bold text-white text-xl" data-id="element-7784">
                      Angel Match Quest
                    </h3>
                    <p className="text-sm text-gray-300" data-id="element-7785">
                      Seductive memory adventure with Angel Mascha
                    </p>
                  </div>
                </div>
                <div className="p-3 flex items-center justify-between" data-id="element-7786">
                  <div className="flex items-center gap-3" data-id="element-7787">
                    <div className="flex items-center gap-1" data-id="element-7788">
                      <Star size={14} className="text-yellow-400" data-id="element-7789" />
                      <span className="text-xs" data-id="element-7790">4.9</span>
                    </div>
                    <div className="text-xs px-2 py-0.5 bg-purple-500/20 rounded-full text-purple-300" data-id="element-7791">
                      Rewards: 💎 + 💗
                    </div>
                  </div>
                  <button className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white animate-pulse" onClick={navigateToAngelMatch} data-id="element-7792">
                    Play Now
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 rounded-xl border border-purple-800/50 overflow-hidden" data-id="element-7793">
              <div className="aspect-video relative" data-id="element-7794">
                <div className="relative rounded-lg overflow-hidden" data-id="element-7795">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tdQoMD6oW7ugtasXgtxV5u/candycrush1.jpg" alt="CandyCrusher" className="w-full h-full object-cover aspect-[4/3]" data-id="element-7796" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-3" data-id="element-7797">
                    <h3 className="font-bold text-white" data-id="element-7798">CandyCrusher</h3>
                    <p className="text-xs text-gray-300" data-id="element-7799">
                      Match-3 gets "harder" with every level 😏
                    </p>
                  </div>
                </div>
                <div className="p-3 flex items-center justify-between" data-id="element-7800">
                  <div className="flex items-center gap-1" data-id="element-7801">
                    <Star size={14} className="text-yellow-400" data-id="element-7802" />
                    <span className="text-xs" data-id="element-7803">4.8</span>
                  </div>
                  <button className="px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-xs text-white" onClick={() => setActiveGame('candy')} data-id="element-7804">
                    Play Now
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-900/40 to-cyan-900/40 rounded-xl border border-blue-800/50 overflow-hidden" data-id="element-7805">
              <div className="aspect-video relative" data-id="element-7806">
                <img src="https://images.unsplash.com/photo-1611996575749-79a3a250f948?q=80&w=2070&auto=format&fit=crop" alt="Pleasure Path" className="w-full h-full object-cover" data-id="element-7807" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-3" data-id="element-7808">
                  <h3 className="font-bold text-white" data-id="element-7809">Pleasure Path</h3>
                  <p className="text-xs text-gray-300" data-id="element-7810">Guide her to ecstasy</p>
                </div>
              </div>
              <div className="p-3 flex items-center justify-between" data-id="element-7811">
                <div className="flex items-center gap-1" data-id="element-7812">
                  <Star size={14} className="text-yellow-400" data-id="element-7813" />
                  <span className="text-xs" data-id="element-7814">4.6</span>
                </div>
                <button className="px-3 py-1 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full text-xs text-white" onClick={() => setActiveGame('path')} data-id="element-7815">
                  Play Now
                </button>
              </div>
            </div>
            <div className="bg-gradient-to-br from-amber-900/40 to-red-900/40 rounded-xl border border-amber-800/50 overflow-hidden" data-id="element-7816">
              <div className="aspect-video relative" data-id="element-7817">
                <img src="https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=2071&auto=format&fit=crop" alt="Strip Dice" className="w-full h-full object-cover" data-id="element-7818" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-3" data-id="element-7819">
                  <h3 className="font-bold text-white" data-id="element-7820">Strip Dice</h3>
                  <p className="text-xs text-gray-300" data-id="element-7821">Roll for rewards</p>
                </div>
              </div>
              <div className="p-3 flex items-center justify-between" data-id="element-7822">
                <div className="flex items-center gap-1" data-id="element-7823">
                  <Star size={14} className="text-yellow-400" data-id="element-7824" />
                  <span className="text-xs" data-id="element-7825">4.9</span>
                </div>
                <button className="px-3 py-1 bg-gradient-to-r from-amber-600 to-red-600 rounded-full text-xs text-white" onClick={() => setActiveGame('dice')} data-id="element-7826">
                  Play Now
                </button>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 rounded-xl border border-green-800/50 overflow-hidden" data-id="element-7827">
              <div className="aspect-video relative" data-id="element-7828">
                <img src="https://images.unsplash.com/photo-1605870445919-838d190e8e1b?q=80&w=2072&auto=format&fit=crop" alt="ToySync Rhythm" className="w-full h-full object-cover" data-id="element-7829" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-3" data-id="element-7830">
                  <div className="flex items-center gap-1 mb-1" data-id="element-7831">
                    <div className="px-1.5 py-0.5 bg-purple-500/50 rounded text-xs" data-id="element-7832">
                      VIP
                    </div>
                  </div>
                  <h3 className="font-bold text-white" data-id="element-7833">ToySync Rhythm</h3>
                  <p className="text-xs text-gray-300" data-id="element-7834">Feel the beat</p>
                </div>
              </div>
              <div className="p-3 flex items-center justify-between" data-id="element-7835">
                <div className="flex items-center gap-1" data-id="element-7836">
                  <Star size={14} className="text-yellow-400" data-id="element-7837" />
                  <span className="text-xs" data-id="element-7838">4.7</span>
                </div>
                <button className="px-3 py-1 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full text-xs text-white" onClick={() => setActiveGame('rhythm')} data-id="element-7839">
                  Play Now
                </button>
              </div>
            </div>
          </div>
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden" data-id="element-7840">
            <div className="p-3 border-b border-gray-800" data-id="element-7841">
              <h3 className="font-medium" data-id="element-7842">Daily Tournaments</h3>
            </div>
            <div className="divide-y divide-gray-800" data-id="element-7843">
              <div className="p-3 flex items-center justify-between" data-id="element-7844">
                <div className="flex items-center gap-3" data-id="element-7845">
                  <div className="w-10 h-10 flex items-center justify-center bg-pink-500/20 rounded-full text-pink-400" data-id="element-7846">
                    🏆
                  </div>
                  <div data-id="element-7847">
                    <p className="text-sm font-medium" data-id="element-7848">Match-3 Marathon</p>
                    <div className="flex items-center gap-1" data-id="element-7849">
                      <Clock size={12} className="text-gray-400" data-id="element-7850" />
                      <p className="text-xs text-gray-400" data-id="element-7851">Ends in 5h 23m</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-7852">
                  <div className="text-xs px-2 py-1 bg-gray-800 rounded-full" data-id="element-7853">
                    <span className="text-pink-400" data-id="element-7854">1st: 500 💗</span>
                  </div>
                  <button className="px-3 py-1.5 bg-pink-600 rounded-full text-xs text-white" onClick={() => setActiveGame('candy')} data-id="element-7855">
                    Join
                  </button>
                </div>
              </div>
              <div className="p-3 flex items-center justify-between" data-id="element-7856">
                <div className="flex items-center gap-3" data-id="element-7857">
                  <div className="w-10 h-10 flex items-center justify-center bg-purple-500/20 rounded-full text-purple-400" data-id="element-7858">
                    🎯
                  </div>
                  <div data-id="element-7859">
                    <p className="text-sm font-medium" data-id="element-7860">
                      Pleasure Path Challenge
                    </p>
                    <div className="flex items-center gap-1" data-id="element-7861">
                      <Clock size={12} className="text-gray-400" data-id="element-7862" />
                      <p className="text-xs text-gray-400" data-id="element-7863">Ends in 2h 45m</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-7864">
                  <div className="text-xs px-2 py-1 bg-gray-800 rounded-full" data-id="element-7865">
                    <span className="text-purple-400" data-id="element-7866">1st: 50 💎</span>
                  </div>
                  <button className="px-3 py-1.5 bg-purple-600 rounded-full text-xs text-white" onClick={() => setActiveGame('path')} data-id="element-7867">
                    Join
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 rounded-xl border border-pink-800/50 p-4" data-id="element-7868">
            <div className="flex items-center gap-3" data-id="element-7869">
              <div className="w-10 h-10 flex items-center justify-center bg-pink-500/20 rounded-full text-pink-400" data-id="element-7870">
                <Gift size={18} data-id="element-7871" />
              </div>
              <div data-id="element-7872">
                <h3 className="font-medium" data-id="element-7873">Game Rewards Boost</h3>
                <p className="text-sm text-gray-300" data-id="element-7874">
                  2× rewards on all games for 24 hours!
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between" data-id="element-7875">
              <div className="flex items-center gap-1" data-id="element-7876">
                <span className="text-purple-400" data-id="element-7877">💎</span>
                <span className="text-sm" data-id="element-7878">5</span>
              </div>
              <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white text-sm font-medium" data-id="element-7879">
                Activate Boost
              </button>
            </div>
          </div>
        </> : <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl border border-gray-800/50 p-4" data-id="element-7880">
          <div className="flex items-center justify-between mb-4" data-id="element-7881">
            <h3 className="font-bold text-lg" data-id="element-7882">
              {activeGame === 'candy' ? 'CandyCrusher' : activeGame === 'path' ? 'Pleasure Path' : activeGame === 'dice' ? 'Strip Dice' : 'ToySync Rhythm'}
            </h3>
            <button className="p-2 rounded-full bg-gray-800 hover:bg-gray-700" onClick={handleClose} data-id="element-7883">
              <X size={18} data-id="element-7884" />
            </button>
          </div>
          {activeGame === 'candy' && <CandyCrusher onComplete={handleGameComplete} data-id="element-7885" />}
          {activeGame === 'path' && <PleasurePath onComplete={handleGameComplete} data-id="element-7886" />}
          {activeGame === 'dice' && <StripDice onComplete={handleGameComplete} data-id="element-7887" />}
          {activeGame === 'rhythm' && <ToySyncRhythm onComplete={handleGameComplete} data-id="element-7888" />}
        </div>}
      {/* Congratulations Modal */}
      {showCongrats && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-id="element-7889">
          <div className="bg-gradient-to-br from-gray-900 to-purple-900/80 rounded-xl border border-pink-500/30 p-6 w-full max-w-md animate-in zoom-in-95 duration-300" data-id="element-7890">
            <div className="flex flex-col items-center text-center" data-id="element-7891">
              <div className="w-20 h-20 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center mb-4" data-id="element-7892">
                <CheckCircle2 size={40} className="text-white" data-id="element-7893" />
              </div>
              <h2 className="text-2xl font-bold mb-2" data-id="element-7894">Congratulations!</h2>
              <p className="text-gray-300 mb-4" data-id="element-7895">You've completed the game</p>
              <div className="flex items-center gap-4 mb-6" data-id="element-7896">
                <div className="text-center" data-id="element-7897">
                  <p className="text-sm text-gray-400" data-id="element-7898">Score</p>
                  <p className="text-xl font-bold" data-id="element-7899">{gameScore}</p>
                </div>
                <div className="h-10 border-r border-gray-700" data-id="element-7900"></div>
                <div className="text-center" data-id="element-7901">
                  <p className="text-sm text-gray-400" data-id="element-7902">Earned</p>
                  <div className="flex items-center justify-center gap-1" data-id="element-7903">
                    <p className="text-xl font-bold" data-id="element-7904">{earnedTokens}</p>
                    <Heart size={18} className="text-pink-500" fill="currentColor" data-id="element-7905" />
                  </div>
                </div>
              </div>
              <button className="w-full py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium mb-3 hover:from-pink-500 hover:to-purple-500 flex items-center justify-center gap-2" onClick={handlePlayAgain} data-id="element-7906">
                <Repeat size={18} data-id="element-7907" />
                <span data-id="element-7908">Play Again</span>
              </button>
              <button className="text-sm text-pink-400 hover:text-pink-300 flex items-center justify-center gap-1" onClick={navigateToRewards} data-id="element-7909">
                <Gift size={14} data-id="element-7910" />
                <span data-id="element-7911">View my rewards</span>
                <ArrowRight size={14} data-id="element-7912" />
              </button>
            </div>
          </div>
        </div>}
    </section>;
};